/**
 * seamField - cross-LOD seam topology from the voxel FIELD, corners from the existing meshes.
 * One face per sign-changing in-plane fine lattice edge, joining its 2 flanking fine cells to the
 * 1-2 coarse cells behind it, plus the high side's suppressed cell-0 band (§5.5).
 * See docs/cross-lod-seam-redesign.md §§2-5.
 */

import {
  CHUNK_SIZE, INV_WEIGHT_MAX_PACKED, MATERIAL_MASK, MATERIAL_SHIFT, MATERIAL_TYPE_LUT,
  MAT_TYPE_SOLID, VOXEL_SCALE, WEIGHT_MASK, WEIGHT_MIN, WEIGHT_SHIFT,
} from '@worldify/shared';
import { LAYER_LIQUID } from './LayerConfig.js';
import { isOutsideForLayer } from './SurfaceNet.js';
import { SKIRT_KIND_NAMES } from '../material/terrainShaders.js';

/** Which side of the plane a strip vertex came from. Read by the shader through `skirtKind`. */
export const SIDE_FINE = 0;
export const SIDE_COARSE = 1;

/** Both fine cells fall in one coarse cell: one triangle. */
export const KIND_FAN = 1;
/** They fall in two: two triangles. */
export const KIND_QUAD = 2;
/** The high side's cell-0 band, dual to a perpendicular edge; four vertices from one side. */
export const KIND_BAND = 3;
/** THE LID: one side capped at the plane where the other level has nothing there. */
export const KIND_LID = 4;

/** `resolveVertex` found the chunk but the cell holds no vertex of this layer. */
export const RESOLVE_NO_VERTEX = -1;
/** ...or there is no displayed chunk over that volume at all. */
export const RESOLVE_NO_CHUNK = -2;

/** A resolved corner: `(source index << SRC_SHIFT) | vertex index`. */
export const SRC_SHIFT = 20;
export const SRC_MASK = (1 << SRC_SHIFT) - 1;

/** Ledger event kinds (opt-in `SeamFieldInput.trace`; see docs §9). */
export const SEAM_EV_TRI = 0, SEAM_EV_SKIP = 1, SEAM_EV_BEGIN = 2;
/** Which pass spoke. */
export const PASS_ZIP = 0, PASS_BRIDGE = 1, PASS_BAND = 2, PASS_LID = 3, PASS_CAP = 4;
/** Why a pass drew nothing. */
export const SKIP_HIDDEN = 0, SKIP_NO_FINE = 1, SKIP_NO_APEX = 2, SKIP_NO_CHUNK = 3,
  SKIP_NO_DATA = 4, SKIP_AMBIG = 5, SKIP_NO_VERTEX = 6;

export const PASS_NAMES = ['zip', 'bridge', 'band', 'lid', 'cap'];
export const SKIP_NAMES = ['hidden', 'noFine', 'noApex', 'noChunk', 'noData', 'ambig', 'noVertex'];

/** One emitter decision. Positions are world; `c1`/`c2` are GLOBAL tangential cells at `unit`. */
export interface SeamTraceEvent {
  t: number;
  pass: number;
  kind: number;
  reason: number;
  pi: number;
  c1: number;
  c2: number;
  /** 0 = the patch's own (fine) resolution, 1 = the owner's. */
  unit: number;
  dir: number;
  /** Zip only: [g1, g2, cell1, cell2] per side — a cell pair that differs is resolveApex's slack. */
  apex: number[];
  pos: number[];
  sides: number[];
}

export type SeamTrace = (e: SeamTraceEvent) => void;

/** One chunk mesh a corner may come from. `slot` is written by the emitter; fresh per strip. */
export interface SeamSource {
  readonly key: string;
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  slot: number;
}

/** One fine participant's 32x32-cell patch of the shared plane. */
export interface SeamPatch {
  /** Global fine-CELL coordinate of the patch origin along t1 = (axis+1)%3 and t2 = (axis+2)%3. */
  readonly base1: number;
  readonly base2: number;
  readonly lod: number;
}

export interface SeamFieldInput {
  readonly ownerLod: number;
  /** 0..5, `axis * 2 + (high ? 1 : 0)`. High = case H, the fine region is across the owner's +axis. */
  readonly face: number;
  readonly layer: number;
  readonly patches: readonly SeamPatch[];
  /** Read by `resolveVertex`'s return value; the resolver may append to it. */
  readonly sources: SeamSource[];
  /** The packed voxel at plane lattice (r1, r2) of patch `pi`, per spec §3; -1 if unavailable. */
  planeVoxel(pi: number, r1: number, r2: number): number;
  /**
   * The packed voxel on the HIGH side's OWN lattice — global tangential (g1, g2) at that side's own
   * resolution, `depth` (0 or 1) steps from the plane into it; -1 if unavailable. Depth 0 is the
   * plane itself, but sampled as that side saw it: in case L that is the coarse owner's data, not
   * the fine sign source `planeVoxel` reads.
   */
  bandVoxel(pi: number, g1: number, g2: number, depth: number): number;
  /** The packed voxel at the COARSE (owner-lod) plane lattice point (q1, q2); -1 if unavailable. */
  coarsePlaneVoxel(q1: number, q2: number): number;
  /** The low side's lattice 31 at global (g1, g2), in its own resolution; -1 if unavailable. */
  lowVoxel(pi: number, g1: number, g2: number): number;
  /** Global cell coords at that side's own resolution -> packed corner, or a RESOLVE_* reason. */
  resolveVertex(side: number, pi: number, g1: number, g2: number): number;
  /** Is there another participant patch one chunk along +t1 (dir 0) / +t2 (dir 1)? */
  hasPatchAt(pi: number, dir: number): boolean;
  /** Does this border row belong to a perpendicular owner? Both would claim it (spec §5.3). */
  cedesRow(pi: number, dir: number, side: number): boolean;
  /** The band's tie-break: a corner line's band is neither owner's. Defaults to `cedesRow`. */
  cedesBandRow?(pi: number, dir: number, side: number): boolean;
  /** Opt-in per-edge ledger. Unset costs one branch per decision and allocates nothing. */
  trace?: SeamTrace;
}

export interface SeamCensus {
  crossings: number;
  triangles: number;
  fans: number;
  quads: number;
  hidden: number;
  /** Patches skipped whole because the plane sign source was not resident (spec §3). */
  noPlaneData: number;
  noFineVertex: number;
  noCoarseApex: number;
  noChunk: number;
  /** Low-boundary band pass (spec §5.5), counted apart from the in-plane duals. */
  bandFaces: number;
  bandHidden: number;
  bandNoVertex: number;
  bandNoData: number;
  /** Coarse-side rim edges at the plane (case H). */
  rimEdges: number;
  /** Rim edges the fine-driven zip never touched — how far the two contours disagree. */
  rimUnzipped: number;
  /** Coarse rim edges closed by the mirror pass, and the ones with no fine vertex to close onto. */
  capFans: number;
  capNoFine: number;
  /** The LID (spec §9): the low side's plane-adjacent layer capped against the other level's sign. */
  lidCoarse: number;
  lidHidden: number;
  lidNoVertex: number;
  lidNoData: number;
}

export const newSeamCensus = (): SeamCensus => ({
  crossings: 0, triangles: 0, fans: 0, quads: 0, hidden: 0,
  noPlaneData: 0, noFineVertex: 0, noCoarseApex: 0, noChunk: 0,
  bandFaces: 0, bandHidden: 0, bandNoVertex: 0, bandNoData: 0,
  rimEdges: 0, rimUnzipped: 0, capFans: 0, capNoFine: 0,
  lidCoarse: 0, lidHidden: 0, lidNoVertex: 0, lidNoData: 0,
});

/**
 * De-indexed triangles in typed arrays with a write cursor, consumed by `SeamStitcher.stripToApron`.
 * `length` is CAPACITY — bound on the counts.
 */
export interface SeamStrip {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  side: Uint8Array;
  src: Int32Array;
  kind: Uint8Array;
  srcOwnerIdx: Int32Array;
  srcOwners: string[];
  vertexCount: number;
  indexCount: number;
}

const STRIP_MIN_VERTS = 4096;

let pooledStrip: SeamStrip | null = null;

const growF32 = (src: Float32Array, cap: number, used: number): Float32Array => {
  const next = new Float32Array(cap);
  next.set(src.subarray(0, used));
  return next;
};
const growI32 = (src: Int32Array, cap: number, used: number): Int32Array => {
  const next = new Int32Array(cap);
  next.set(src.subarray(0, used));
  return next;
};
const growU8 = (src: Uint8Array, cap: number, used: number): Uint8Array => {
  const next = new Uint8Array(cap);
  next.set(src.subarray(0, used));
  return next;
};

function reserveVerts(s: SeamStrip, need: number): void {
  if (s.side.length >= need) return;
  let cap = s.side.length || STRIP_MIN_VERTS;
  while (cap < need) cap *= 2;
  const used = s.vertexCount;
  s.positions = growF32(s.positions, cap * 3, used * 3);
  s.normals = growF32(s.normals, cap * 3, used * 3);
  s.side = growU8(s.side, cap, used);
  s.kind = growU8(s.kind, cap, used);
  s.src = growI32(s.src, cap, used);
  s.srcOwnerIdx = growI32(s.srcOwnerIdx, cap, used);
}

function reserveIndices(s: SeamStrip, need: number): void {
  if (s.indices.length >= need) return;
  let cap = s.indices.length || STRIP_MIN_VERTS;
  while (cap < need) cap *= 2;
  const next = new Uint32Array(cap);
  next.set(s.indices.subarray(0, s.indexCount));
  s.indices = next;
}

/** The pooled strip, rewound. Valid only until the next call; nothing may retain one. */
export function emptySeamStrip(): SeamStrip {
  if (pooledStrip === null) {
    pooledStrip = {
      positions: new Float32Array(STRIP_MIN_VERTS * 3),
      normals: new Float32Array(STRIP_MIN_VERTS * 3),
      indices: new Uint32Array(STRIP_MIN_VERTS * 2),
      side: new Uint8Array(STRIP_MIN_VERTS),
      src: new Int32Array(STRIP_MIN_VERTS),
      kind: new Uint8Array(STRIP_MIN_VERTS),
      srcOwnerIdx: new Int32Array(STRIP_MIN_VERTS),
      srcOwners: [],
      vertexCount: 0,
      indexCount: 0,
    };
  }
  const s = pooledStrip;
  s.vertexCount = 0;
  s.indexCount = 0;
  s.srcOwners.length = 0;
  emittedTri.clear();
  posId.clear();
  posCount = 0;
  posOx = NaN;
  refId.clear();
  return s;
}

const LAT = CHUNK_SIZE + 1;
const latSign = new Int8Array(LAT * LAT);
const latOpaque = new Uint8Array(LAT * LAT);
const rimSign = new Int8Array(LAT * LAT);
const rimOpaque = new Uint8Array(LAT * LAT);
/** The owner's own plane signs, whole-lattice: the lid reads them outside any one patch. */
const ownerSign = new Int8Array(LAT * LAT);
const ownerOpaque = new Uint8Array(LAT * LAT);
let ownerLatOk = false;
let ownerBase1 = 0, ownerBase2 = 0;

/** The coarse rim ledger, case H only: edges the seam drew, against the coarse mesh's own rim. */
const drawnEdge = new Map<number, number>();
const rimPairs = new Set<number>();
/** One coarse rim edge, 7 numbers: patch, dir, its two lattice coords, sign, and its two vertices. */
const rimEntries: number[] = [];
/** A coarse vertex -> a fine one the zip joined it to; the mirror pass reuses that edge. */
const coarseFine = new Map<number, number>();
/** Plane points the per-patch case-L band already covered. */
const bandDone = new Set<number>();
/** Triangles drawn, by sorted position triple: two zip edges can resolve to the same corners. */
const emittedTri = new Map<number, Set<number>>();
/**
 * One id per POSITION: a lattice vertex retained in two chunks' margins arrives as two refs.
 * Keyed NUMERICALLY, in millimetres relative to the face's first source origin: xy packed into one
 * number, z in a nested map. A face spans a few chunks of its owner at most, so 24 bits an axis holds
 * it; the template string this replaces was the largest string allocator in a flush.
 */
const posId = new Map<number, Map<number, number>>();
let posCount = 0;
let posOx = NaN, posOy = 0, posOz = 0;
const POS_STRIDE = 0x1000000;
const refId = new Map<number, number>();

function vertexId(sources: readonly SeamSource[], ref: number): number {
  const got = refId.get(ref);
  if (got !== undefined) return got;
  const s = sources[ref >>> SRC_SHIFT];
  const v = (ref & SRC_MASK) * 3;
  if (posOx !== posOx) { posOx = s.ox; posOy = s.oy; posOz = s.oz; }
  const qx = Math.round((s.positions[v] + s.ox - posOx) * 1000) + (POS_STRIDE >> 1);
  const qy = Math.round((s.positions[v + 1] + s.oy - posOy) * 1000) + (POS_STRIDE >> 1);
  const qz = Math.round((s.positions[v + 2] + s.oz - posOz) * 1000);
  const kxy = qx * POS_STRIDE + qy;
  let byZ = posId.get(kxy);
  if (byZ === undefined) { byZ = new Map(); posId.set(kxy, byZ); }
  let id = byZ.get(qz);
  if (id === undefined) { id = posCount++; byZ.set(qz, id); }
  refId.set(ref, id);
  return id;
}
/** The ledger, and the context every event is stamped with. Null unless the caller asked for it. */
let trace: SeamTrace | null = null;
let ctxPass = PASS_ZIP, ctxPi = 0, ctxC1 = 0, ctxC2 = 0, ctxUnit = 0, ctxDir = 0;
const ctxApex: number[] = [];

function worldOf(sources: readonly SeamSource[], ref: number, into: number[]): void {
  const s = sources[ref >>> SRC_SHIFT];
  const v = (ref & SRC_MASK) * 3;
  into.push(s.positions[v] + s.ox, s.positions[v + 1] + s.oy, s.positions[v + 2] + s.oz);
}

function traceEvent(t: number, kind: number, reason: number, pos: number[], sides: number[]): void {
  trace!({
    t, pass: ctxPass, kind, reason, pi: ctxPi, c1: ctxC1, c2: ctxC2, unit: ctxUnit, dir: ctxDir,
    apex: ctxApex.slice(), pos, sides,
  });
}

let trackParity = false;
const pairKey = (a: number, b: number): number =>
  (a < b ? a : b) * 0x40000000 + (a < b ? b : a);
const bump = (m: Map<number, number>, a: number, b: number): void => {
  if (a === b) return;
  const k = pairKey(a, b);
  m.set(k, (m.get(k) ?? 0) + 1);
};

const filledOpaque = (voxel: number): boolean =>
  ((voxel >> WEIGHT_SHIFT) & WEIGHT_MASK) * INV_WEIGHT_MAX_PACKED + WEIGHT_MIN > 0
  && MATERIAL_TYPE_LUT[(voxel >> MATERIAL_SHIFT) & MATERIAL_MASK] === MAT_TYPE_SOLID;

function push(
  out: SeamStrip, sources: readonly SeamSource[], ref: number, side: number, kind: number,
): void {
  const s = sources[ref >>> SRC_SHIFT];
  const v = ref & SRC_MASK;
  if (s.slot < 0) {
    s.slot = out.srcOwners.length;
    out.srcOwners.push(s.key);
  }
  reserveVerts(out, out.vertexCount + 1);
  const w = out.vertexCount;
  const p = w * 3;
  const sp = v * 3;
  out.positions[p] = s.positions[sp] + s.ox;
  out.positions[p + 1] = s.positions[sp + 1] + s.oy;
  out.positions[p + 2] = s.positions[sp + 2] + s.oz;
  out.normals[p] = s.normals[sp] ?? 0;
  out.normals[p + 1] = s.normals[sp + 1] ?? 0;
  out.normals[p + 2] = s.normals[sp + 2] ?? 0;
  out.side[w] = side;
  out.src[w] = v;
  out.srcOwnerIdx[w] = s.slot;
  out.kind[w] = kind;
  out.vertexCount = w + 1;
}

/** Triangles emitted per kind since load — cumulative, so "is this kind still emitted at all" is a
 *  number rather than a squint at a hillside. Read by the F3 panel through `seamKindSummary`. */
const kindTally = new Uint32Array(8);

/** One count per kind, space separated, kind 1 first. The panel pairs it with SKIRT_KIND_NAMES. */
export function seamKindSummary(): string {
  let out = '';
  for (let k = 1; k < SKIRT_KIND_NAMES.length; k++) out += `${out ? ' ' : ''}${kindTally[k]}`;
  return out;
}

/** The high id of the last `triKey` — the third of the sorted triple, read straight after it. */
let lastKeyHi = 0;

/** Sorted position-id triple, or -1 when two of the three coincide (a collapsed triangle). */
function triKey(sources: readonly SeamSource[], ar: number, br: number, cr: number): number {
  let lo = vertexId(sources, ar), md = vertexId(sources, br), hi = vertexId(sources, cr);
  if (lo === md || md === hi || lo === hi) return -1;
  if (lo > md) { const t = lo; lo = md; md = t; }
  if (md > hi) { const t = md; md = hi; hi = t; }
  if (lo > md) { const t = lo; lo = md; md = t; }
  lastKeyHi = hi;
  return lo * 0x2000000 + md;
}

function tri(
  out: SeamStrip, sources: readonly SeamSource[],
  ar: number, as: number, br: number, bs: number, cr: number, cs: number, kind: number,
): boolean {
  const dk = triKey(sources, ar, br, cr);
  if (dk < 0) return false;
  const hi = lastKeyHi;
  let seen = emittedTri.get(dk);
  if (seen === undefined) { seen = new Set(); emittedTri.set(dk, seen); }
  else if (seen.has(hi)) return false;
  seen.add(hi);
  kindTally[kind]++;
  // Fine-fine edges are already even; the band plays the a-directed role, not the zip's.
  if (trackParity && kind !== KIND_BAND) {
    if (as !== SIDE_FINE || bs !== SIDE_FINE) bump(drawnEdge, ar, br);
    if (bs !== SIDE_FINE || cs !== SIDE_FINE) bump(drawnEdge, br, cr);
    if (cs !== SIDE_FINE || as !== SIDE_FINE) bump(drawnEdge, cr, ar);
    const rs = [ar, br, cr], ss = [as, bs, cs];
    for (let i = 0; i < 3; i++) {
      if (ss[i] !== SIDE_COARSE) continue;
      for (let j = 0; j < 3; j++) if (ss[j] === SIDE_FINE) coarseFine.set(rs[i], rs[j]);
    }
  }
  const base = out.vertexCount;
  push(out, sources, ar, as, kind);
  push(out, sources, br, bs, kind);
  push(out, sources, cr, cs, kind);
  reserveIndices(out, out.indexCount + 3);
  out.indices[out.indexCount] = base;
  out.indices[out.indexCount + 1] = base + 1;
  out.indices[out.indexCount + 2] = base + 2;
  out.indexCount += 3;
  if (trace) {
    const pos: number[] = [];
    worldOf(sources, ar, pos); worldOf(sources, br, pos); worldOf(sources, cr, pos);
    traceEvent(SEAM_EV_TRI, kind, -1, pos, [as, bs, cs]);
  }
  return true;
}

/** Where a coarse apex may be found, nearest first: `regionWallTop` dilates a wall. */
const APEX_OFFSETS = new Int8Array([
  0, 0,
  1, 0, -1, 0, 0, 1, 0, -1,
  1, 1, 1, -1, -1, 1, -1, -1,
]);

/** The cell `resolveApex` actually found its answer in. */
let apexCell1 = 0, apexCell2 = 0;


/** Liquid only: an apex a coarse cell away in Y is another sheet; zipping it hangs a curtain. */
function sameSheet(input: SeamFieldInput, ref: number, near: number): boolean {
  if (input.layer !== LAYER_LIQUID || near < 0) return true;
  const a = input.sources[ref >>> SRC_SHIFT];
  const b = input.sources[near >>> SRC_SHIFT];
  const ya = a.positions[(ref & SRC_MASK) * 3 + 1] + a.oy;
  const yb = b.positions[(near & SRC_MASK) * 3 + 1] + b.oy;
  return Math.abs(ya - yb) <= VOXEL_SCALE * (1 << input.ownerLod);
}

/** One answer per coarse cell: the slack is direction-preferring, and two answers is a hole. */
const apexMemo = new Map<number, number>();
const apexMemoCell = new Map<number, number>();
const cellKey = (c1: number, c2: number): number => (c1 + 0x8000) * 0x10000 + (c2 + 0x8000);

/** The coarse vertex at (g1, g2) or its nearest; `along`/`out` break the equidistant tie. */
function resolveApex(
  input: SeamFieldInput, pi: number, g1: number, g2: number, along: number, out: number,
  near: number,
): number {
  const key = cellKey(g1, g2);
  const memo = apexMemo.get(key);
  if (memo !== undefined) {
    if (!sameSheet(input, memo, near)) return RESOLVE_NO_VERTEX;
    const cell = apexMemoCell.get(key)!;
    apexCell1 = ((cell / 0x10000) | 0) - 0x8000;
    apexCell2 = (cell % 0x10000) - 0x8000;
    return memo;
  }
  let miss = RESOLVE_NO_CHUNK;
  for (let k = 0; k < APEX_OFFSETS.length; k += 2) {
    const u = APEX_OFFSETS[k] * out, v = APEX_OFFSETS[k + 1];
    const c1 = g1 + (along === 0 ? u : v), c2 = g2 + (along === 0 ? v : u);
    const got = input.resolveVertex(SIDE_COARSE, pi, c1, c2);
    if (got >= 0 && !sameSheet(input, got, near)) {
      miss = RESOLVE_NO_VERTEX;
      continue;
    }
    if (got >= 0) {
      apexCell1 = c1; apexCell2 = c2;
      apexMemo.set(key, got);
      apexMemoCell.set(key, cellKey(c1, c2));
      return got;
    }
    if (got > miss) miss = got;
  }
  return miss;
}

/**
 * One in-plane fine edge, `dir` 0 = t1-directed, 1 = t2-directed, min corner at lattice (r1, r2).
 * Its four cells ARE the mesher's quad — idx0..idx3 at (u,v) offsets (0,0), (-1,0), (0,-1), (-1,-1)
 * for (u,v) = ((f+1)%3, (f+2)%3) — so `mask & 1` (min corner outside) picks the winding unchanged.
 * A t1-directed edge has v = axis and a t2-directed one has u = axis: the only difference.
 */
function emitEdge(
  input: SeamFieldInput, out: SeamStrip, census: SeamCensus,
  pi: number, d: number, base1: number, base2: number, caseH: boolean,
  dir: number, r1: number, r2: number,
): void {
  const i0 = r1 * LAT + r2;
  const i1 = dir === 0 ? i0 + LAT : i0 + 1;
  const outAtMin = latSign[i0] === 1;
  if (outAtMin === (latSign[i1] === 1)) return;
  census.crossings++;
  if (trace) {
    ctxPass = PASS_ZIP; ctxPi = pi; ctxC1 = base1 + r1; ctxC2 = base2 + r2;
    ctxUnit = 0; ctxDir = dir; ctxApex.length = 0;
  }
  if (latOpaque[outAtMin ? i0 : i1] !== 0) {
    census.hidden++;
    if (trace) traceEvent(SEAM_EV_SKIP, 0, SKIP_HIDDEN, [], []);
    return;
  }

  let a1 = r1, a2 = r2, b1 = r1, b2 = r2;
  if (dir === 0) b2 = r2 - 1; else b1 = r1 - 1;

  const fa = input.resolveVertex(SIDE_FINE, pi, base1 + a1, base2 + a2);
  const fb = input.resolveVertex(SIDE_FINE, pi, base1 + b1, base2 + b2);
  if (fa < 0 || fb < 0) {
    const noChunk = fa === RESOLVE_NO_CHUNK || fb === RESOLVE_NO_CHUNK;
    if (noChunk) census.noChunk++; else census.noFineVertex++;
    if (trace) traceEvent(SEAM_EV_SKIP, 0, noChunk ? SKIP_NO_CHUNK : SKIP_NO_FINE, [], []);
    return;
  }
  const ga1 = (base1 + a1) >> d, ga2 = (base2 + a2) >> d;
  const gb1 = (base1 + b1) >> d, gb2 = (base2 + b2) >> d;
  const same = ga1 === gb1 && ga2 === gb2;
  const outward = outAtMin ? -1 : 1;
  const ca = resolveApex(input, pi, ga1, ga2, dir, outward, fa);
  const ac1 = apexCell1, ac2 = apexCell2;
  const cb = same ? ca : resolveApex(input, pi, gb1, gb2, dir, outward, fb);
  const bc1 = same ? ac1 : apexCell1, bc2 = same ? ac2 : apexCell2;
  if (trace) {
    ctxApex.length = 0;
    ctxApex.push(ga1, ga2, ac1, ac2, gb1, gb2, bc1, bc2);
  }
  if (ca < 0 || cb < 0) {
    const noChunk = ca === RESOLVE_NO_CHUNK || cb === RESOLVE_NO_CHUNK;
    if (noChunk) census.noChunk++; else census.noCoarseApex++;
    if (trace) {
      const pos: number[] = [];
      worldOf(input.sources, fa, pos); worldOf(input.sources, fb, pos);
      traceEvent(SEAM_EV_SKIP, 0, noChunk ? SKIP_NO_CHUNK : SKIP_NO_APEX, pos,
        [SIDE_FINE, SIDE_FINE]);
    }
    return;
  }

  const nearSide = caseH ? SIDE_FINE : SIDE_COARSE;
  const farSide = caseH ? SIDE_COARSE : SIDE_FINE;
  const q00 = caseH ? fa : ca, q0m = caseH ? fb : cb;
  const qm0 = caseH ? ca : fa, qmm = caseH ? cb : fb;

  const e0r = q00, e0s = nearSide;
  const e3r = qmm, e3s = farSide;
  const e1r = dir === 0 ? q0m : qm0, e1s = dir === 0 ? nearSide : farSide;
  const e2r = dir === 0 ? qm0 : q0m, e2s = dir === 0 ? farSide : nearSide;

  const kind = same ? KIND_FAN : KIND_QUAD;
  const src = input.sources;
  let n = 0;
  if (outAtMin) {
    if (tri(out, src, e1r, e1s, e0r, e0s, e3r, e3s, kind)) n++;
    if (tri(out, src, e2r, e2s, e3r, e3s, e0r, e0s, kind)) n++;
  } else {
    if (tri(out, src, e2r, e2s, e0r, e0s, e3r, e3s, kind)) n++;
    if (tri(out, src, e1r, e1s, e3r, e3s, e0r, e0s, kind)) n++;
  }
  if (n === 0) return;
  census.triangles += n;
  if (same) census.fans++; else census.quads++;

  // Diagonal apexes jump a cell; a rim-graph bridge for the rest fired 0 times (dead end).
  if (ca === cb || ac1 === bc1 || ac2 === bc2) return;
  const m = tryApex(input, pi, ac1, bc2, ca, cb) ?? tryApex(input, pi, bc1, ac2, ca, cb);
  if (m === undefined) return;
  const first = (outAtMin ? 1 : 0) === (1 ^ dir ^ (caseH ? 1 : 0));
  const c = SIDE_COARSE;
  if (trace) ctxPass = PASS_BRIDGE;
  if (tri(out, src, first ? ca : cb, c, first ? cb : ca, c, m, c, kind)) census.triangles++;
}

/** Stamp the ledger with one coarse rim entry (7 numbers from `rimEntries`). */
function capContext(input: SeamFieldInput, e: number): void {
  const pi = rimEntries[e];
  const d = input.ownerLod - input.patches[pi].lod;
  ctxPass = PASS_CAP; ctxPi = pi; ctxUnit = 1; ctxDir = rimEntries[e + 1];
  ctxC1 = (input.patches[pi].base1 >> d) + rimEntries[e + 2];
  ctxC2 = (input.patches[pi].base2 >> d) + rimEntries[e + 3];
  ctxApex.length = 0;
}

/** The vertex in one intermediate coarse cell, unless it is one of the two the quad already has. */
function tryApex(
  input: SeamFieldInput, pi: number, c1: number, c2: number, ca: number, cb: number,
): number | undefined {
  const v = input.resolveVertex(SIDE_COARSE, pi, c1, c2);
  return v < 0 || v === ca || v === cb ? undefined : v;
}

/** The fine vertex under a coarse rim edge, for a run the zip never reached. */
function seedFine(
  input: SeamFieldInput, pi: number, dir: number, q1: number, q2: number,
): number {
  const d = input.ownerLod - input.patches[pi].lod;
  const g1 = ((input.patches[pi].base1 >> d) + q1) << d;
  const g2 = ((input.patches[pi].base2 >> d) + q2) << d;
  const k = 1 << d;
  for (let i = 0; i < k; i++) {
    for (let s = 0; s < 2; s++) {
      const o = s === 0 ? i : -1 - i;
      const got = input.resolveVertex(SIDE_FINE, pi,
        dir === 0 ? g1 + o : g1, dir === 0 ? g2 : g2 + o);
      if (got >= 0) return got;
    }
  }
  return RESOLVE_NO_VERTEX;
}

/** The mirror of the fine-driven quad, for a coarse rim pair no fine crossing straddled. */
function emitRimFan(
  input: SeamFieldInput, out: SeamStrip, census: SeamCensus,
  dir: number, outAtMin: boolean, va: number, vb: number, seed: number, caseH: boolean,
): boolean {
  const src = input.sources;
  let fa = coarseFine.get(va);
  let fb = coarseFine.get(vb);
  if (fa === undefined && fb === undefined) {
    if (seed < 0) return false;
    fa = seed; fb = seed;
  } else if (fa === undefined) fa = fb;
  else if (fb === undefined) fb = fa;
  // Case L swaps near and far, so the zip traverses its coarse-coarse edge the other way round.
  const flip = ((dir === 0) === outAtMin) === caseH;
  const c = SIDE_COARSE, f0 = fa as number, f1 = fb as number;
  const ok = flip
    ? tri(out, src, va, c, vb, c, f0, SIDE_FINE, KIND_QUAD)
    : tri(out, src, va, c, f0, SIDE_FINE, vb, c, KIND_QUAD);
  if (!ok) return false;
  census.capFans++;
  census.triangles++;
  // Both ends keep the partner the zip gave them, or the far end's edge is left single.
  if (f1 !== f0) {
    const ok2 = flip
      ? tri(out, src, vb, c, f1, SIDE_FINE, f0, SIDE_FINE, KIND_QUAD)
      : tri(out, src, f0, SIDE_FINE, f1, SIDE_FINE, vb, c, KIND_QUAD);
    if (ok2) { census.capFans++; census.triangles++; }
  }
  coarseFine.set(va, f0);
  coarseFine.set(vb, f1);
  return true;
}

/** The high side's suppressed cell-0 band (§5.5); the other level's sample here cost 383 opens. */
function emitBandPoint(
  input: SeamFieldInput, out: SeamStrip, census: SeamCensus,
  pi: number, layer: number, side: number, g1: number, g2: number,
  planeOut: number, planeOpq: number,
): void {
  if (trace) {
    ctxPass = PASS_BAND; ctxPi = pi; ctxC1 = g1; ctxC2 = g2;
    ctxUnit = side === SIDE_COARSE ? 1 : 0; ctxDir = -1; ctxApex.length = 0;
  }
  const v0 = input.bandVoxel(pi, g1, g2, 0);
  const v1 = input.bandVoxel(pi, g1, g2, 1);
  if (v0 < 0 || v1 < 0) {
    census.bandNoData++;
    if (trace) traceEvent(SEAM_EV_SKIP, KIND_BAND, SKIP_NO_DATA, [], []);
    return;
  }
  // The plane end takes the ZIP's sign where a patch supplies one — the mirror of case H's lid.
  const outAtPlane = planeOut >= 0 ? planeOut === 1 : isOutsideForLayer(v0, layer);
  if (outAtPlane === isOutsideForLayer(v1, layer)) return;
  const opqAtPlane = planeOpq >= 0 ? planeOpq === 1 : filledOpaque(v0);
  if (outAtPlane ? opqAtPlane : filledOpaque(v1)) {
    census.bandHidden++;
    if (trace) traceEvent(SEAM_EV_SKIP, KIND_BAND, SKIP_HIDDEN, [], []);
    return;
  }

  const i0 = input.resolveVertex(side, pi, g1, g2);
  const i1 = input.resolveVertex(side, pi, g1 - 1, g2);
  const i2 = input.resolveVertex(side, pi, g1, g2 - 1);
  const i3 = input.resolveVertex(side, pi, g1 - 1, g2 - 1);
  if (i0 < 0 || i1 < 0 || i2 < 0 || i3 < 0) {
    census.bandNoVertex++;
    if (trace) traceEvent(SEAM_EV_SKIP, KIND_BAND, SKIP_NO_VERTEX, [], []);
    return;
  }
  const src = input.sources;
  let n = 0;
  if (outAtPlane) {
    if (tri(out, src, i1, side, i0, side, i3, side, KIND_BAND)) n++;
    if (tri(out, src, i2, side, i3, side, i0, side, KIND_BAND)) n++;
  } else {
    if (tri(out, src, i2, side, i0, side, i3, side, KIND_BAND)) n++;
    if (tri(out, src, i1, side, i3, side, i0, side, KIND_BAND)) n++;
  }
  if (n > 0) census.bandFaces++;
}

/** The lid (§9): the low side capped against the other level's sign, and only where it has none. */
function emitLidPoint(
  input: SeamFieldInput, out: SeamStrip, census: SeamCensus,
  pi: number, side: number, g1: number, g2: number, outAtMin: boolean, hidden: boolean,
): void {
  if (trace) {
    ctxPass = PASS_LID; ctxPi = pi; ctxC1 = g1; ctxC2 = g2; ctxUnit = 1; ctxDir = -1;
    ctxApex.length = 0;
  }
  if (hidden) {
    census.lidHidden++;
    if (trace) traceEvent(SEAM_EV_SKIP, KIND_LID, SKIP_HIDDEN, [], []);
    return;
  }
  // Capping onto cell 30 where 31 has no vertex measured worse (zoom coarse 211 -> 236).
  const i0 = input.resolveVertex(side, pi, g1, g2);
  const i1 = input.resolveVertex(side, pi, g1 - 1, g2);
  const i2 = input.resolveVertex(side, pi, g1, g2 - 1);
  const i3 = input.resolveVertex(side, pi, g1 - 1, g2 - 1);
  if (i0 < 0 || i1 < 0 || i2 < 0 || i3 < 0) {
    census.lidNoVertex++;
    if (trace) traceEvent(SEAM_EV_SKIP, KIND_LID, SKIP_NO_VERTEX, [], []);
    return;
  }
  const src = input.sources;
  let n = 0;
  if (outAtMin) {
    if (tri(out, src, i1, side, i0, side, i3, side, KIND_LID)) n++;
    if (tri(out, src, i2, side, i3, side, i0, side, KIND_LID)) n++;
  } else {
    if (tri(out, src, i2, side, i0, side, i3, side, KIND_LID)) n++;
    if (tri(out, src, i1, side, i3, side, i0, side, KIND_LID)) n++;
  }
  if (n === 0) return;
  census.triangles += n;
  census.lidCoarse++;
}

/** The coarse side's own rim at the plane — every crossing case H's mask took out of the union. */
function buildCoarseRim(
  input: SeamFieldInput,
  pi: number, d: number, base1: number, base2: number,
  first1: number, first2: number, last1: number, last2: number,
): void {
  const layer = input.layer;
  const n = CHUNK_SIZE >> d;
  const b1 = base1 >> d, b2 = base2 >> d;
  for (let q1 = 0; q1 <= n; q1++) {
    for (let q2 = 0; q2 <= n; q2++) {
      const v = input.coarsePlaneVoxel(b1 + q1, b2 + q2);
      if (v < 0) return;
      const at = q1 * LAT + q2;
      rimSign[at] = isOutsideForLayer(v, layer) ? 1 : 0;
      rimOpaque[at] = filledOpaque(v) ? 1 : 0;
    }
  }
  for (let dir = 0; dir < 2; dir++) {
    const e1 = dir === 0 ? n : n + last1;
    const e2 = dir === 0 ? n + last2 : n;
    for (let q1 = dir === 1 ? first1 : 0; q1 < e1; q1++) {
      for (let q2 = dir === 0 ? first2 : 0; q2 < e2; q2++) {
        const i0 = q1 * LAT + q2;
        const i1 = dir === 0 ? i0 + LAT : i0 + 1;
        const outAtMin = rimSign[i0] === 1;
        if (outAtMin === (rimSign[i1] === 1)) continue;
        const c1 = dir === 0 ? q1 : q1 - 1, c2 = dir === 0 ? q2 - 1 : q2;
        const va = input.resolveVertex(SIDE_COARSE, pi, b1 + q1, b2 + q2);
        const vb = input.resolveVertex(SIDE_COARSE, pi, b1 + c1, b2 + c2);
        if (va < 0 || vb < 0) continue;
        if (va === vb) continue;
        // The coarse mesh's own hidden gate first: a pair it never drew is not an edge the mask took.
        if (rimOpaque[outAtMin ? i0 : i1] !== 0) continue;
        rimPairs.add(pairKey(va, vb));
        rimEntries.push(pi, dir, q1, q2, outAtMin ? 1 : 0, va, vb);
      }
    }
  }
}

/**
 * Emit one (owner face, layer) seam into `out`. Non-reentrant: the sign lattice is module-pooled.
 */
export function emitSeamFace(
  input: SeamFieldInput, out: SeamStrip, census: SeamCensus = newSeamCensus(),
): SeamCensus {
  const caseH = (input.face & 1) === 1;
  const layer = input.layer;
  trace = input.trace ?? null;
  if (trace) {
    ctxPass = PASS_ZIP; ctxPi = 0; ctxC1 = 0; ctxC2 = 0; ctxUnit = 0; ctxDir = -1;
    ctxApex.length = 0;
    traceEvent(SEAM_EV_BEGIN, 0, -1, [], []);
  }
  apexMemo.clear();
  apexMemoCell.clear();
  drawnEdge.clear();
  coarseFine.clear();
  bandDone.clear();
  rimPairs.clear();
  rimEntries.length = 0;
  trackParity = caseH;
  /** The side of the plane whose adjacent cell layer is its chunk's cell 0. */
  const highSide = caseH ? SIDE_FINE : SIDE_COARSE;
  ownerLatOk = false;
  if (input.patches.length > 0) {
    const d0 = input.ownerLod - input.patches[0].lod;
    ownerBase1 = Math.floor((input.patches[0].base1 >> d0) / CHUNK_SIZE) * CHUNK_SIZE;
    ownerBase2 = Math.floor((input.patches[0].base2 >> d0) / CHUNK_SIZE) * CHUNK_SIZE;
    ownerLatOk = true;
    for (let q1 = 0; q1 <= CHUNK_SIZE && ownerLatOk; q1++) {
      for (let q2 = 0; q2 <= CHUNK_SIZE; q2++) {
        const v = input.coarsePlaneVoxel(ownerBase1 + q1, ownerBase2 + q2);
        if (v < 0) { ownerLatOk = false; census.lidNoData++; break; }
        const at = q1 * LAT + q2;
        ownerSign[at] = isOutsideForLayer(v, layer) ? 1 : 0;
        ownerOpaque[at] = filledOpaque(v) ? 1 : 0;
      }
    }
  }
  if (caseH) {
    for (let pi = 0; pi < input.patches.length; pi++) {
      const d = input.ownerLod - input.patches[pi].lod;
      if (d < 1) continue;
      buildCoarseRim(input, pi, d, input.patches[pi].base1, input.patches[pi].base2,
        input.cedesRow(pi, 0, 0) ? 1 : 0, input.cedesRow(pi, 1, 0) ? 1 : 0,
        input.hasPatchAt(pi, 0) || input.cedesRow(pi, 0, 1) ? 0 : 1,
        input.hasPatchAt(pi, 1) || input.cedesRow(pi, 1, 1) ? 0 : 1);
    }
  }
  for (let pi = 0; pi < input.patches.length; pi++) {
    const patch = input.patches[pi];
    const d = input.ownerLod - patch.lod;
    if (d < 1) continue;
    const base1 = patch.base1, base2 = patch.base2;

    let ok = true;
    for (let r1 = 0; r1 <= CHUNK_SIZE && ok; r1++) {
      for (let r2 = 0; r2 <= CHUNK_SIZE; r2++) {
        const voxel = input.planeVoxel(pi, r1, r2);
        if (voxel < 0) { ok = false; break; }
        const at = r1 * LAT + r2;
        latSign[at] = isOutsideForLayer(voxel, layer) ? 1 : 0;
        latOpaque[at] = filledOpaque(voxel) ? 1 : 0;
      }
    }
    const first1 = input.cedesRow(pi, 0, 0) ? 1 : 0;
    const first2 = input.cedesRow(pi, 1, 0) ? 1 : 0;
    const last1 = input.hasPatchAt(pi, 0) || input.cedesRow(pi, 0, 1) ? 0 : 1;
    const last2 = input.hasPatchAt(pi, 1) || input.cedesRow(pi, 1, 1) ? 0 : 1;
    if (ok) {
      const r2End = CHUNK_SIZE + last2;
      const r1End = CHUNK_SIZE + last1;
      for (let r1 = 0; r1 < CHUNK_SIZE; r1++) {
        for (let r2 = first2; r2 < r2End; r2++) {
          emitEdge(input, out, census, pi, d, base1, base2, caseH, 0, r1, r2);
        }
      }
      for (let r1 = first1; r1 < r1End; r1++) {
        for (let r2 = 0; r2 < CHUNK_SIZE; r2++) {
          emitEdge(input, out, census, pi, d, base1, base2, caseH, 1, r1, r2);
        }
      }
    } else census.noPlaneData++;

    // Pass 2 at the high side's OWN resolution, so case L's 2^d patches name the same plane points.
    const hb = caseH ? 0 : d;
    const b1 = base1 >> hb, b2 = base2 >> hb;
    // The band has its own row rules — it does not carry the zip's min(axis) tie-break.
    const cedes = input.cedesBandRow ?? input.cedesRow;
    const bf1 = cedes(pi, 0, 0) ? 1 : 0;
    const bf2 = cedes(pi, 1, 0) ? 1 : 0;
    const j1End = (CHUNK_SIZE >> hb) + (input.hasPatchAt(pi, 0) || cedes(pi, 0, 1) ? 0 : 1);
    const j2End = (CHUNK_SIZE >> hb) + (input.hasPatchAt(pi, 1) || cedes(pi, 1, 1) ? 0 : 1);
    for (let j1 = bf1; j1 < j1End; j1++) {
      for (let j2 = bf2; j2 < j2End; j2++) {
        if (!caseH) bandDone.add(cellKey(b1 + j1, b2 + j2));
        // The coincident FINE plane point: identity at case H, the patch's own even point at case L.
        const fl1 = ((b1 + j1) << hb) - base1, fl2 = ((b2 + j2) << hb) - base2;
        const inLat = ok && fl1 >= 0 && fl2 >= 0 && fl1 <= CHUNK_SIZE && fl2 <= CHUNK_SIZE;
        const fine = inLat ? latSign[fl1 * LAT + fl2] : -1;
        emitBandPoint(input, out, census, pi, layer, highSide, b1 + j1, b2 + j2, fine,
          inLat ? latOpaque[fl1 * LAT + fl2] : -1);
      }
    }

    // Case H only: the case-L mirror has no vertex to cap a submerged sheet with.
    if (!ownerLatOk || !ok || !caseH) continue;
    const c1 = base1 >> d, c2 = base2 >> d;
    const k1End = (CHUNK_SIZE >> d) + last1;
    const k2End = (CHUNK_SIZE >> d) + last2;
    for (let k1 = first1; k1 < k1End; k1++) {
      for (let k2 = first2; k2 < k2End; k2++) {
        const g1 = c1 + k1, g2 = c2 + k2;
        const lq1 = g1 - ownerBase1, lq2 = g2 - ownerBase2;
        if (lq1 < 0 || lq2 < 0 || lq1 > CHUNK_SIZE || lq2 > CHUNK_SIZE) continue;
        const ownOut = ownerSign[lq1 * LAT + lq2] === 1;
        const at = ((g1 << d) - base1) * LAT + ((g2 << d) - base2);
        if (ownOut === (latSign[at] === 1)) continue;
        const lowV = input.lowVoxel(pi, g1, g2);
        if (lowV < 0) {
          census.lidNoData++;
          if (trace) {
            ctxPass = PASS_LID; ctxPi = pi; ctxC1 = g1; ctxC2 = g2; ctxUnit = 1; ctxDir = -1;
            ctxApex.length = 0;
            traceEvent(SEAM_EV_SKIP, KIND_LID, SKIP_NO_DATA, [], []);
          }
          continue;
        }
        // The low mesh's own 31->32 quad is these same four vertices.
        if (isOutsideForLayer(lowV, layer) !== ownOut) continue;
        emitLidPoint(input, out, census, pi, SIDE_COARSE, g1, g2, ownOut,
          ownOut ? filledOpaque(lowV) : latOpaque[at] !== 0);
      }
    }
  }

  // The rest of the owner's cell-0 band: the patches tile only where a finer chunk is displayed.
  if (!caseH && input.patches.length > 0) {
    const d0 = input.ownerLod - input.patches[0].lod;
    const ob1 = Math.floor((input.patches[0].base1 >> d0) / CHUNK_SIZE) * CHUNK_SIZE;
    const ob2 = Math.floor((input.patches[0].base2 >> d0) / CHUNK_SIZE) * CHUNK_SIZE;
    for (let j1 = 0; j1 < CHUNK_SIZE; j1++) {
      for (let j2 = 0; j2 < CHUNK_SIZE; j2++) {
        if (bandDone.has(cellKey(ob1 + j1, ob2 + j2))) continue;
        emitBandPoint(input, out, census, 0, layer, highSide, ob1 + j1, ob2 + j2, -1, -1);
      }
    }
  }
  if (caseH) {
    // A chain closes one pass at a time, from whichever end the zip already reached.
    for (let round = 0; round < CHUNK_SIZE; round++) {
      let did = false;
      for (let e = 0; e < rimEntries.length; e += 7) {
        const va = rimEntries[e + 5], vb = rimEntries[e + 6];
        if ((drawnEdge.get(pairKey(va, vb)) ?? 0) !== 0) continue;
        if (trace) capContext(input, e);
        if (emitRimFan(input, out, census, rimEntries[e + 1], rimEntries[e + 4] === 1, va, vb, -1,
          caseH)) {
          did = true;
        }
      }
      // A run the zip never touched has no vertex to start from: seed one edge from the field.
      if (!did) {
        for (let e = 0; e < rimEntries.length && !did; e += 7) {
          const va = rimEntries[e + 5], vb = rimEntries[e + 6];
          if ((drawnEdge.get(pairKey(va, vb)) ?? 0) !== 0) continue;
          const seed = seedFine(input, rimEntries[e], rimEntries[e + 1],
            rimEntries[e + 2], rimEntries[e + 3]);
          if (trace) capContext(input, e);
          if (seed < 0) {
            census.capNoFine++;
            if (trace) traceEvent(SEAM_EV_SKIP, KIND_QUAD, SKIP_NO_FINE, [], []);
            continue;
          }
          did = emitRimFan(input, out, census, rimEntries[e + 1], rimEntries[e + 4] === 1,
            va, vb, seed, caseH);
        }
      }
      if (!did) break;
    }
    for (const p of rimPairs) if ((drawnEdge.get(p) ?? 0) === 0) census.rimUnzipped++;
    census.rimEdges += rimPairs.size;
  }
  trackParity = false;
  trace = null;
  return census;
}
